import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { LinkFormattingService } from '../services/link-formatting.service';

@Injectable()
export class UserGeneratedTextInterceptor implements HttpInterceptor {
  private readonly httpsPattern = /https:\/\/[^\s<>"']+/i;
  private readonly skipKeyPattern = /url|lien|href|token|path|apikey|secret|password|image|photo|avatar|preview|apercu|contenu|contenttype/i;

  constructor(private linkFormattingService: LinkFormattingService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (
      !['POST', 'PUT', 'PATCH'].includes(req.method) ||
      !req.body ||
      req.url.includes('/lien/formater') ||
      req.url.includes('/lien/verifier')
    ) {
      return next.handle(req);
    }

    const parsedBody = this.parseBody(req.body);
    if (parsedBody === null) {
      return next.handle(req);
    }

    return from(this.formatBody(parsedBody)).pipe(
      switchMap(body => {
        const nextBody = typeof req.body === 'string' ? JSON.stringify(body) : body;
        return next.handle(req.clone({ body: nextBody }));
      })
    );
  }

  private parseBody(body: unknown): unknown | null {
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as unknown;
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    }

    if (typeof body === 'object') {
      return body;
    }

    return null;
  }

  private async formatBody(body: unknown): Promise<unknown> {
    if (Array.isArray(body)) {
      return Promise.all(body.map(item => this.formatBody(item)));
    }

    if (!body || typeof body !== 'object') {
      return body;
    }

    if (!this.isPlainObject(body)) {
      return body;
    }

    const record = body as Record<string, unknown>;
    const formatted: Record<string, unknown> = { ...record };

    for (const [key, value] of Object.entries(record)) {
      if (typeof value === 'string') {
        formatted[key] = await this.formatStringValue(key, value);
      } else if (value && typeof value === 'object') {
        formatted[key] = await this.formatBody(value);
      }
    }

    return formatted;
  }

  private isPlainObject(value: object): boolean {
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || prototype === Object.prototype;
  }

  private async formatStringValue(key: string, value: string): Promise<string> {
    if (!this.shouldFormat(key, value)) {
      return value;
    }

    const estHtml = value.includes('<') && value.includes('>');
    return this.linkFormattingService.formatForSubmit(value, estHtml);
  }

  private shouldFormat(key: string, value: string): boolean {
    if (!value || !this.httpsPattern.test(value)) {
      return false;
    }

    if (this.skipKeyPattern.test(key) && this.isPureUrl(value.trim())) {
      return false;
    }

    return true;
  }

  private isPureUrl(value: string): boolean {
    return /^https:\/\/[^\s<>"']+$/.test(value);
  }
}
